import { Box } from '@mui/material'
import { type lookup } from '../../../core/types'
import { SelectWithLabel } from '../../../Components/SelectWithLabel'
import { CheckBoxWithLabel } from '../../../Components/CheckBox'
import { useEffect, useState } from 'react'
import { Modal } from '../../../Components/Modal'
import { sendGet, sendPost } from '../../../hooks/use-fetch'
import { toast } from 'react-toastify'
import { BannerModal } from '../../../Components/Modal/BannerModal'

interface ImportSessionsProps {
  sessionBlockId: string | undefined
  open: boolean
  onClose: () => void
  onSubmit: () => void
}

export interface ImportSessionsDetailsView {
  importIntoBlockId: string | undefined
  importFromBlockId: number | null
  includeSeatAllocations: boolean
  includeStaff: boolean
  includeSchedules: boolean
}

export interface ImportSessionsConfirmationModel {
  sessionCount: number
  importIntoSessionBlockName: string
}

export function ImportSessionsModal (props: ImportSessionsProps): JSX.Element {
  const [model, setModel] = useState<ImportSessionsDetailsView>({ importIntoBlockId: props.sessionBlockId, importFromBlockId: null, includeSeatAllocations: true, includeSchedules: true, includeStaff: true })
  const [sessionBlockList, setSessionBlockList] = useState<lookup[]>([])
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [confirmationModel, setConfirmationModel] = useState<ImportSessionsConfirmationModel>({ sessionCount: 0, importIntoSessionBlockName: '' })

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      const resp = await sendGet(`/SessionList/GetPreviousSessionBlocks?sessionBlockId=${props.sessionBlockId ?? ''}`, {})
      if (resp.success) {
        setSessionBlockList(resp.response)
      }
    }
    void loadData()
  }, [props.sessionBlockId])

  const valueChanged = (field: string, value: any): void => {
    setModel({ ...model, [field]: value })
  }

  const showConfirmation = async (): Promise<void> => {
    if (model.importFromBlockId === null) {
      toast.error('Session block is required')
    } else {
      const resp = await sendGet(`/SessionList/GetImportSessionConfirmationModel?importFromSessionBlockId=${model.importFromBlockId}&importIntoSessionBlockId=${model.importIntoBlockId ?? 0}`, {})
      if (resp.success) {
        if (resp.response.isValid === false) {
          toast.error('Invalid session block selection')
        } else if (resp.response.sessionCount > 0) {
          setConfirmationModel({ sessionCount: resp.response.sessionCount, importIntoSessionBlockName: resp.response.importIntoSessionBlockName })
          setShowConfirmationModal(true)
        } else {
          toast.error('No sessions to import')
        }
      } else {
        resp.error.forEach(error => {
          toast.error(error)
        })
      }
    }
  }

  const onSubmit = async (): Promise<void> => {
    const resp = await sendPost('/SessionList/ImportSessions', model)
    if (resp.success) {
      setShowConfirmationModal(false)
      toast.success('Session import successful')
      props.onSubmit()
    } else {
      resp.error.forEach(error => {
        toast.error(error)
      })
    }
  }

  const formContent = <Box>
    <SelectWithLabel
      width='100%'
      label='Session Block'
      name='importFromBlockId'
      required={true}
      onChange={(evt: any) => { valueChanged('importFromBlockId', evt.target.value) }}
      options={sessionBlockList}
      value={model.importFromBlockId ?? ''}
    />
    <CheckBoxWithLabel
      label={'Include Seat Allocations'}
      name={'includeSeatAllocations'}
      value={model.includeSeatAllocations}
      setValue={(evt: boolean) => { valueChanged('includeSeatAllocations', evt) }}
      edge={'start'}
      dataTestId={'includeSeatAllocationsCheckbox'}
    />
    <CheckBoxWithLabel
      label={'Include Staff'}
      name={'includeStaff'}
      value={model.includeStaff}
      setValue={(evt: boolean) => { valueChanged('includeStaff', evt) }}
      edge={'start'}
      dataTestId={'includeStaffCheckbox'}
    />
    <CheckBoxWithLabel
      label={'Include Schedules'}
      name={'includeSchedules'}
      value={model.includeSchedules}
      setValue={(evt: boolean) => { valueChanged('includeSchedules', evt) }}
      edge={'start'}
      dataTestId={'includeSchedulesCheckbox'}
    />
  </Box>

  const confirmationContent = <Box>
    <Box>This action will import <b>{confirmationModel.sessionCount}</b> sessions to the <b>{confirmationModel.importIntoSessionBlockName}</b> session block and you will not be able to import again. Continue?</Box>
  </Box>

  return <>
    <Modal
      maxWidth='sm'
      open={props.open}
      title={'Import Sessions From Previous Block'}
      confirmationContent={formContent}
      confirmButtonText='Continue'
      cancelButtonText='Cancel'
      onClose={props.onClose}
      onConfirm={showConfirmation}
    />

    {showConfirmationModal && <BannerModal
      open={showConfirmationModal}
      title={'Confirm Import'}
      content={confirmationContent}
      confirmButtonText={'Confirm'}
      cancelButtonText={'Cancel'}
      onClose={() => { setShowConfirmationModal(false) }}
      onConfirm={onSubmit}
    />}
  </>
}
